import { Fragment, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../api/client'
import { useForbiddenGuard, useWriteForbiddenGuard } from '../api/guard'
import { writeErrorMessage } from '../api/problems'
import { useIsSuperAdmin } from '../api/tier'
import {
  MATRIX_QUERY_KEY,
  draftFrom,
  fieldErrors,
  formatValue,
  kindLabel,
  matrixPath,
  parseDraft,
  planFeaturesPath,
  type FeatureValue,
  type MatrixPlan,
  type MatrixResponse,
  type PlanFeaturesResult,
  type RegistryFeature,
  type ValueDraft,
} from '../api/entitlements'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { ValueEditor } from '../components/ValueEditor'
import './screens.css'

const FAMILY_LABELS: Record<string, string> = { website: 'Website plans', appsumo: 'AppSumo plans' }
const familyLabel = (f: string) => FAMILY_LABELS[f] ?? `${f} plans`
const planLabel = (p: MatrixPlan) => p.display_name || p.name

interface Editing {
  plan: MatrixPlan
  feature: RegistryFeature
  draft: ValueDraft
  error: string | null
}

interface Pending {
  plan: MatrixPlan
  feature: RegistryFeature
  from: FeatureValue | undefined
  to: FeatureValue
}

/**
 * Plans: the plan × feature matrix of one plan family (entitlements E4).
 *
 * Columns are the family's plans (tier order) under one family header; rows are the
 * registry's features grouped by area, with kind and remedy. A super admin edits a
 * cell inline, confirms, and exactly one PUT goes out carrying only that key. Everyone
 * else sees the matrix read-only. No prices.
 */
export function PlansView() {
  const { isSuperAdmin } = useIsSuperAdmin()
  const [family, setFamily] = useState('website')
  const [editing, setEditing] = useState<Editing | null>(null)
  const [pending, setPending] = useState<Pending | null>(null)
  const [okMsg, setOkMsg] = useState<string | null>(null)
  const qc = useQueryClient()

  const matrix = useQuery<MatrixResponse>({
    queryKey: [MATRIX_QUERY_KEY, family],
    queryFn: () => apiFetch<MatrixResponse>(matrixPath(family)),
  })
  useForbiddenGuard(matrix.error)

  const save = useMutation({
    mutationFn: (p: Pending) =>
      apiFetch<PlanFeaturesResult>(planFeaturesPath(p.plan.name), {
        method: 'PUT',
        body: JSON.stringify({ [p.feature.key]: p.to }),
      }),
    onSuccess: (data, p) => {
      setEditing(null)
      setOkMsg(
        data.changed
          ? `${planLabel(p.plan)}: ${p.feature.label} is now ${formatValue(p.feature, p.to)}.`
          : `${planLabel(p.plan)}: ${p.feature.label} was already ${formatValue(p.feature, p.to)}; nothing changed.`,
      )
      void qc.invalidateQueries({ queryKey: [MATRIX_QUERY_KEY] })
      void qc.invalidateQueries({ queryKey: ['admin-plans'] })
    },
    onError: () => setOkMsg(null),
  })
  useWriteForbiddenGuard(save.error)

  const data = matrix.data
  const plans = data?.plans ?? []
  const features = data?.features ?? []
  const groups: { group: string; rows: RegistryFeature[] }[] = []
  for (const f of features) {
    const last = groups[groups.length - 1]
    if (last && last.group === f.group) last.rows.push(f)
    else groups.push({ group: f.group, rows: [f] })
  }
  const families = data?.families ?? ['website', 'appsumo']
  const colCount = 3 + plans.length
  const saveErrors = save.isError ? fieldErrors(save.error) : []

  function startEdit(plan: MatrixPlan, feature: RegistryFeature) {
    save.reset()
    setOkMsg(null)
    setEditing({ plan, feature, draft: draftFrom(feature, plan.features[feature.key]?.value), error: null })
  }

  function submitEdit() {
    if (!editing) return
    const parsed = parseDraft(editing.feature, editing.draft)
    if (!parsed.ok) {
      setEditing({ ...editing, error: parsed.error })
      return
    }
    const current = editing.plan.features[editing.feature.key]
    setPending({ plan: editing.plan, feature: editing.feature, from: current?.value, to: parsed.value })
  }

  return (
    <section className="scr" aria-labelledby="plans-heading">
      <div className="screen-head">
        <h2 id="plans-heading">Plans</h2>
        <div className="scr-actions" role="group" aria-label="Plan family">
          {families.map((f) => (
            <button
              key={f}
              type="button"
              className={'screen-btn' + (f === family ? ' screen-btn-primary' : '')}
              aria-pressed={f === family}
              onClick={() => {
                setFamily(f)
                setEditing(null)
                setOkMsg(null)
              }}
            >
              {familyLabel(f)}
            </button>
          ))}
        </div>
      </div>

      <p className="scr-note">
        What each plan includes. A workspace override wins over its plan for one key (see
        Workspaces). No prices are kept here.
        {isSuperAdmin
          ? ' Edits apply to every workspace on the plan as soon as they are saved.'
          : ' Only super admins can edit the matrix.'}
      </p>

      {matrix.isLoading && <p className="scr-note">Loading matrix…</p>}
      {matrix.isError && (matrix.error as { status?: number }).status !== 403 && (
        <p className="ws-error" role="alert">
          Failed to load the plan matrix.
        </p>
      )}
      {save.isError && (
        <div className="ws-error" role="alert">
          <p>{writeErrorMessage(save.error)}</p>
          {saveErrors.length > 0 && (
            <ul>
              {saveErrors.map((e) => (
                <li key={e.key}>
                  <code>{e.key}</code>: {e.message}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {okMsg && <p className="scr-ok">{okMsg}</p>}

      {matrix.isSuccess && plans.length === 0 && (
        <p className="scr-note">No {familyLabel(family).toLowerCase()} are defined yet.</p>
      )}

      {plans.length > 0 && (
        <table className="scr-table" aria-label={`${familyLabel(family)} matrix`}>
          <thead>
            <tr>
              <th colSpan={3} />
              <th colSpan={plans.length} scope="colgroup">
                {familyLabel(family)}
              </th>
            </tr>
            <tr>
              <th scope="col">Feature</th>
              <th scope="col">Kind</th>
              <th scope="col">Remedy</th>
              {plans.map((p) => (
                <th key={p.name} scope="col">
                  {planLabel(p)}
                  <div className="scr-muted">
                    {p.billing_kind}
                    {!p.is_active && ' · inactive'}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <Fragment key={g.group}>
                <tr className="scr-row--muted">
                  <th colSpan={colCount} scope="rowgroup">
                    {g.group}
                  </th>
                </tr>
                {g.rows.map((f) => (
                  <tr key={f.key}>
                    <th scope="row">
                      {f.label}
                      <div>
                        <code className="scr-mono">{f.key}</code>
                      </div>
                    </th>
                    <td>{kindLabel(f)}</td>
                    <td className="scr-muted">{f.remedy}</td>
                    {plans.map((p) => {
                      const cell = p.features[f.key]
                      const isEditing =
                        editing?.plan.name === p.name && editing.feature.key === f.key
                      return (
                        <td key={p.name} data-testid={`cell-${p.name}-${f.key}`}>
                          {isEditing && editing ? (
                            <div className="scr-form">
                              <ValueEditor
                                feature={f}
                                draft={editing.draft}
                                label={`${f.label} for ${planLabel(p)}`}
                                onChange={(draft) => setEditing({ ...editing, draft, error: null })}
                              />
                              {editing.error && (
                                <span className="scr-err" role="alert">
                                  {editing.error}
                                </span>
                              )}
                              <div className="scr-actions">
                                <button
                                  type="button"
                                  className="screen-btn screen-btn-primary"
                                  disabled={save.isPending}
                                  onClick={submitEdit}
                                >
                                  Save
                                </button>
                                <button
                                  type="button"
                                  className="screen-btn"
                                  onClick={() => setEditing(null)}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <span>
                                {cell ? (
                                  formatValue(f, cell.value)
                                ) : (
                                  <span className="scr-muted">
                                    default ({formatValue(f, f.default)})
                                  </span>
                                )}
                              </span>
                              {cell?.updated_by && (
                                <span className="scr-badge scr-badge--changed" title="Edited by an admin">
                                  edited
                                </span>
                              )}
                              {isSuperAdmin && (
                                <button
                                  type="button"
                                  className="screen-btn"
                                  aria-label={`Edit ${f.key} for ${p.name}`}
                                  disabled={save.isPending}
                                  onClick={() => startEdit(p, f)}
                                >
                                  Edit
                                </button>
                              )}
                            </>
                          )}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}

      {pending && (
        <ConfirmDialog
          label="Confirm matrix change"
          confirmText="Save change"
          onCancel={() => setPending(null)}
          onConfirm={() => {
            const p = pending
            setPending(null)
            save.mutate(p)
          }}
        >
          <p>
            Change <strong>{pending.feature.label}</strong> on{' '}
            <strong>{planLabel(pending.plan)}</strong> from{' '}
            {pending.from === undefined
              ? `default (${formatValue(pending.feature, pending.feature.default)})`
              : formatValue(pending.feature, pending.from)}{' '}
            to <strong>{formatValue(pending.feature, pending.to)}</strong>?
          </p>
          <p className="scr-note">
            Every workspace on this plan gets the new value on its next request, unless it has
            an override for this key.
          </p>
        </ConfirmDialog>
      )}
    </section>
  )
}
